import React, { useState, useRef, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { 
  IconButton, 
  Tooltip, 
  Modal, 
  Box, 
  Typography, 
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faTimes, faSort } from '@fortawesome/free-solid-svg-icons';
import { formatNumberWithPersianSeparators } from '../utils/formatNumber';

const modalStyle = {
  direction: 'rtl',
  fontFamily: 'Vazir, IRANSans, Tahoma, Arial',
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '95%', // Adjusted for better mobile fit
  maxWidth: 600,
  maxHeight: '95vh', // Limit height for scrolling on small screens
  overflowY: 'auto', // Enable vertical scrolling
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  outline: 'none',
};

const tableStyles = {
  '& .MuiTableCell-root': {
    fontFamily: 'Vazir, IRANSans, Tahoma, Arial',
    fontSize: '1rem',
    textAlign: 'right',
    direction: 'rtl'
  },
  '& .MuiTypography-root': {
    fontFamily: 'Vazir, IRANSans, Tahoma, Arial'
  }
};

const AppointmentPDF = ({ appointmentData, userInfo, statusContent }) => {
  const [open, setOpen] = useState(false);
  const [pdfPreviewData, setPdfPreviewData] = useState(null);
  const contentRef = useRef(null);
  const [fontLoaded, setFontLoaded] = useState(false);

  // Load Vazir font for PDF
  useEffect(() => {
    // Add Vazir font to document for preview
    const style = document.createElement('style');
    style.textContent = `
      @font-face {
        font-family: 'Vazir';
        src: url('/Fonts/vazir-font/Vazir.eot');
        src: url('/Fonts/vazir-font/Vazir.eot?#iefix') format('embedded-opentype'),
             url('/Fonts/vazir-font/Vazir.woff2') format('woff2'),
             url('/Fonts/vazir-font/Vazir.woff') format('woff'),
             url('/Fonts/vazir-font/Vazir.ttf') format('truetype');
        font-weight: normal;
        font-style: normal;
      }
      
      @font-face {
        font-family: 'Vazir';
        src: url('/Fonts/vazir-font/Vazir-Bold.eot');
        src: url('/Fonts/vazir-font/Vazir-Bold.eot?#iefix') format('embedded-opentype'),
             url('/Fonts/vazir-font/Vazir-Bold.woff2') format('woff2'),
             url('/Fonts/vazir-font/Vazir-Bold.woff') format('woff'),
             url('/Fonts/vazir-font/Vazir-Bold.ttf') format('truetype');
        font-weight: bold;
        font-style: normal;
      }
    `;
    document.head.appendChild(style);

    // Load Vazir font for PDF
    const loadVazirFont = async () => {
      try {
        // Fetch the Vazir font file
        const response = await fetch('/Fonts/vazir-font/Vazir.ttf');
        const fontData = await response.arrayBuffer();
        
        // Convert ArrayBuffer to Base64
        const base64Font = btoa(
          new Uint8Array(fontData)
            .reduce((data, byte) => data + String.fromCharCode(byte), '')
        );
        
        // Add the font to jsPDF
        const doc = new jsPDF();
        doc.addFileToVFS('Vazir.ttf', base64Font);
        doc.addFont('Vazir.ttf', 'Vazir', 'normal');
        
        setFontLoaded(true);
        console.log('Vazir font loaded successfully for PDF');
      } catch (error) {
        console.error('Error loading Vazir font for PDF:', error);
      }
    };
    
    loadVazirFont();
  }, []);

  const handleOpen = () => {
    generatePDFPreviewData();
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const toPersianNumber = (text) => {
    return text.toString().replace(/\d/g, d => ['۰','۱','۲','۳','۴','۵','۶','۷','۸','۹'][d]);
  };

  const formatTurnTimeWithPeriod = (timeString) => {
    if (!timeString) return '';
    const raw = String(timeString).trim();

    const ampmMatch = raw.match(/am|pm/i);
    const hasMeridiem = Boolean(ampmMatch);
    const isPM = hasMeridiem ? /pm/i.test(ampmMatch[0]) : false;

    const timePart = raw.replace(/[^0-9:]/g, '');
    const [hRaw = '00', mRaw = '00'] = timePart.split(':');

    let hour = parseInt(hRaw, 10);
    let minute = parseInt(mRaw, 10);

    if (!Number.isFinite(hour)) hour = 0;
    if (!Number.isFinite(minute)) minute = 0;

    if (hasMeridiem) {
      if (isPM && hour < 12) hour += 12;
      if (!isPM && hour === 12) hour = 0; // 12 AM -> 00
    }

    const hourStr = String(hour).padStart(2, '0');
    const minuteStr = String(minute).padStart(2, '0');
    const normalized = `${hourStr}:${minuteStr}`;
    return toPersianNumber(normalized);
  };

  const generatePDFPreviewData = () => {
    // Extract the text content from the statusContent prop
    const statusText = statusContent && statusContent.props && statusContent.props.label ? statusContent.props.label : 'ثبت شده';

    const data = {
      style: {
        fontFamily: 'IRANSans, Tahoma, Arial',
        direction: 'rtl',
        textAlign: 'right'  
      },
      title: 'رسید نوبت  درمانگاه  فوق تخصصی چشم پزشکی دکتر صلوتی ',
      details: [
        { label: 'نام پزشک', value: appointmentData.DoctorName, sortKey: appointmentData.DoctorName },
        { label: 'نام بیمار', value: userInfo.fullName || 'نامشخص', sortKey: userInfo.fullName },
        { label: 'نام خدمت', value: appointmentData.ServiceName, sortKey: appointmentData.ServiceName },
        { label: 'مبلغ پرداختی', value: `${formatNumberWithPersianSeparators(appointmentData.ServicePrice)} ریال`, sortKey: appointmentData.ServicePrice },
        { label: 'کد رهگیری', value: toPersianNumber(appointmentData.TransactionNum), sortKey: appointmentData.TransactionNum },
        { label: 'تاریخ مراجعه', value: new Intl.DateTimeFormat('fa-IR').format(new Date(appointmentData.TurnDate)), sortKey: new Date(appointmentData.TurnDate) },
        { label: 'ساعت مراجعه', value: formatTurnTimeWithPeriod(appointmentData.TurnTime), sortKey: appointmentData.TurnTime },
        { label: 'وضعیت نوبت', value: statusText },
      
        
      ]
    };
    setPdfPreviewData(data);
  };



  const handleDownloadPDF = async () => {
    if (!contentRef.current) return;

    try {
      // Show loading state if needed
      console.log('Generating PDF...');
      
      const canvas = await html2canvas(contentRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        fontFamily: 'Vazir, IRANSans, Tahoma, Arial',
        allowTaint: true,
        removeContainer: true,
        letterRendering: true
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        putOnlyUsedFonts: true,
        floatPrecision: 16
      });

      // Set PDF metadata with encoding for better RTL support
      pdf.setProperties({
        title: 'رسید نوبت  درمانگاه  فوق تخصصی چشم پزشکی دکتر صلوتی ',
        subject: 'رسید نوبت',
        author: 'سیستم نوبت‌دهی',
        keywords: 'نوبت، پزشک، رسید',
        creator: 'سیستم نوبت‌دهی'
      });

      // Set right-to-left mode
      pdf.setR2L(true);
      
      // Use Vazir font if loaded
      if (fontLoaded) {
        pdf.setFont('Vazir');
        console.log('Using Vazir font for PDF');
      }

      // Calculate dimensions and paginate if content is taller than one page
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth; // fit to page width
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      // First page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Additional pages
      while (heightLeft > 0) {
        position = heightLeft - imgHeight; // shift image up to show next slice
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Save the PDF (all pages)
      pdf.save(`appointment-${appointmentData.TransactionNum}.pdf`);
      console.log('PDF generated successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  return (
    <>
      <Tooltip title="مشاهده و دانلود PDF نوبت">
        <IconButton onClick={handleOpen} color="primary" size="large">
          <FontAwesomeIcon icon={faFilePdf} />
        </IconButton>
      </Tooltip>

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="pdf-preview-modal"
        aria-describedby="pdf-preview-description"
      >
        <Box sx={modalStyle}>
          <Box display="flex" justifyContent="center" alignItems="center" mb={3} position="relative">
            <Typography variant="h6" component="h2" sx={{ fontFamily: 'Vazir, IRANSans, Tahoma, Arial', textAlign: 'center', flex: 1 }}>
              پیش‌ نمایش رسید نوبت
            </Typography>
            <IconButton onClick={handleClose} size="small" sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)' }}>
              <FontAwesomeIcon icon={faTimes} />
            </IconButton>
          </Box>

          <Box ref={contentRef} sx={{ 
            bgcolor: 'white', 
            p: { xs: 2, md: 4 }, // Responsive padding
            borderRadius: 1, 
            fontFamily: 'Vazir, IRANSans, Tahoma, Arial',
            maxWidth: '100%',
            boxShadow: '0 0 10px rgba(0,0,0,0.1)',
            border: '1px solid #ddd',
            overflowX: 'auto', // Allow horizontal scrolling for tables if needed
          }}>
            {pdfPreviewData && (
              <div style={{ direction: 'rtl', textAlign: 'right' }}>
                <div style={{ 
                  textAlign: 'center', 
                  marginBottom: '2rem', 
                  borderBottom: '2px solid #3f51b5', 
                  paddingBottom: '1rem' 
                }}>
                  <h1 style={{ 
                    fontFamily: 'Vazir, IRANSans, Tahoma, Arial', 
                    fontSize: '1.5rem', 
                    fontWeight: 'bold',
                    color: '#3f51b5'
                  }}>{pdfPreviewData.title}</h1>
                </div>
                
                <div style={{ marginBottom: '2rem' }}>
                  <table style={{ 
                    width: '100%', 
                    borderCollapse: 'collapse', 
                    tableLayout: 'fixed',
                    direction: 'rtl',
                    fontFamily: 'Vazir, IRANSans, Tahoma, Arial',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere'
                  }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f5f5f5' }}>
                        <th style={{ 
                          padding: '0.75rem', 
                          borderBottom: '1px solid #ddd', 
                          textAlign: 'right', 
                          fontWeight: 'bold',
                          width: '40%',
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere'
                        }}> </th>
                        <th style={{ 
                          padding: '0.75rem', 
                          borderBottom: '1px solid #ddd', 
                          textAlign: 'right',
                          width: '60%',
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere'
                        }}> </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pdfPreviewData.details.map((detail, index) => (
                        <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                          <td style={{ 
                            padding: '0.75rem', 
                            borderBottom: '1px solid #eee', 
                            fontWeight: 'bold',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere'
                          }}>{detail.label}</td>
                          <td style={{ 
                            padding: '0.75rem', 
                            borderBottom: '1px solid #eee',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere'
                          }}>{detail.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <div style={{ 
                  textAlign: 'center', 
                  marginTop: '2rem', 
                  borderTop: '1px solid #eee',
                  paddingTop: '1rem',
                  color: '#777',
                  fontSize: '0.85rem'
                }}>
                  <p>این سند به صورت الکترونیکی تولید شده است</p>
                  <div style={{ 
                    marginTop: '0.5rem',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <span>تاریخ صدور: {new Intl.DateTimeFormat('fa-IR').format(new Date())}</span>
                    <span>آدرس : فارس - شیراز - زرگری - چهارراه ستارخان کوچه 19</span>
                  </div>
                </div>
              </div>
            )}
          </Box>

          <Box mt={3} display="flex" justifyContent="center" gap={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleDownloadPDF}
              // startIcon={<FontAwesomeIcon icon={faFilePdf} />}
              sx={{ minWidth: 200, fontFamily: 'Vazir, IRANSans, Tahoma, Arial' }}
            >
              دانلود  
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default AppointmentPDF; 